/**
 * Expand/collapse toggle for long Contact Form 7 acceptance checkbox text.
 *
 * Opt-in per field, authored directly in the CF7 form editor (not a block attribute):
 * right after an `[acceptance]` tag, add
 * `<span class="wpcf7-acceptance-expand" data-expand-label="Zobacz więcej" data-collapse-label="Zwiń" hidden></span>`.
 *
 * Truncation itself is pure CSS (`-webkit-line-clamp` + an absolutely positioned overlay,
 * see _cf7-form.scss) — this script only decides whether the toggle is needed (real
 * overflow) and flips the expanded/collapsed state on click.
 */

/**
 * Whether a clamped label's text actually overflows two lines.
 *
 * With `-webkit-line-clamp` active, `scrollHeight` reports the same (clamped)
 * value as `clientHeight` in Chromium — there's no "hidden extra content" to
 * measure directly. Instead, clone the label without the clamp and measure its
 * natural height as a sibling right after the original (so it keeps matching
 * the same `.wpcf7-form .wpcf7-acceptance .wpcf7-list-item-label` selector
 * chain our CSS relies on — moving it out to `document.body`/`.wpcf7-form`
 * root loses that ancestor match and silently measures with the wrong
 * font-size/line-height). `flex: none` is required on the clone, otherwise
 * it still inherits the flex item's `flex-basis` sizing from the row and
 * reports the same (wrong) clamped height even with `position: absolute`.
 *
 * @param {HTMLElement} label Element carrying the 2-line `-webkit-line-clamp`.
 * @return {boolean} Whether the full text is taller than the clamped box.
 */
const isOverflowing = ( label ) => {

	const clone = label.cloneNode( true );

	clone.style.position = 'absolute';
	clone.style.visibility = 'hidden';
	clone.style.display = 'block';
	clone.style.webkitLineClamp = 'unset';
	clone.style.overflow = 'visible';
	clone.style.flex = 'none';
	clone.style.width = `${ label.getBoundingClientRect().width }px`;

	label.after( clone );
	const overflows = clone.scrollHeight > label.clientHeight + 1;
	clone.remove();

	return overflows;

};

/**
 * Wire up a single acceptance field's expand/collapse toggle, if it overflows.
 *
 * @param {HTMLElement} wrap Element carrying [data-name] for the acceptance field.
 * @return {void}
 */
const initSingleAcceptanceExpand = ( wrap ) => {

	const toggle = wrap.nextElementSibling;

	if ( ! toggle?.matches( '.wpcf7-acceptance-expand' ) ) {

		return;

	}

	const label = wrap.querySelector( '.wpcf7-list-item-label' );

	if ( ! label || ! isOverflowing( label ) ) {

		return; // Fits within the clamp as-is — no toggle needed.

	}

	label.append( toggle );
	toggle.hidden = false;
	toggle.textContent = toggle.dataset.expandLabel;
	// Authored as a plain <span> in the CF7 form editor — make it a real keyboard-operable
	// button here rather than relying on the form author to remember role/tabindex.
	toggle.setAttribute( 'role', 'button' );
	toggle.tabIndex = 0;

	/**
	 * Toggle the expanded/collapsed state, activated by click or Enter/Space.
	 *
	 * @param {Event} event Triggering click or keydown event.
	 * @return {void}
	 */
	const toggleExpanded = ( event ) => {

		// The toggle lives inside a <label>, so a plain click/Space would also toggle
		// the associated checkbox — stop that, this activation is only for expand/collapse.
		event.preventDefault();

		const isExpanded = label.classList.toggle( 'is-expanded' );

		toggle.textContent = isExpanded ? toggle.dataset.collapseLabel : toggle.dataset.expandLabel;

	};

	toggle.addEventListener( 'click', toggleExpanded );
	toggle.addEventListener( 'keydown', ( event ) => {

		if ( event.key === 'Enter' || event.key === ' ' ) {

			toggleExpanded( event );

		}

	} );

};

/**
 * Wire up every CF7 acceptance field/expand-toggle pair found on the page.
 *
 * @return {void}
 */
const initAcceptanceExpand = () => {

	document.querySelectorAll( '.wpcf7-acceptance' ).forEach( ( acceptance ) => {

		const wrap = acceptance.closest( '.wpcf7-form-control-wrap' );

		if ( wrap ) {

			initSingleAcceptanceExpand( wrap );

		}

	} );

};

document.addEventListener( 'DOMContentLoaded', initAcceptanceExpand );
